import React, { useMemo } from 'react';

const W = 212;
const H = 96;
const PAD_X = 20;
const PAD_Y = 18;
const CHART_W = W - PAD_X * 2;
const CHART_H = H - PAD_Y * 2;

// Triadic – gegenüberstehend im Farbkreis
const DIFF_COLORS = [
    '#4ade80', // ★1 Leicht – Grün  (~120°)
    '#fbbf24', // ★2 Mittel – Amber (~45°)
    '#f87171', // ★3 Schwer – Rot   (~0°)
];

function BalanceCurve({ questions = [] }) {
    const counts = useMemo(() => {
        const c = [0, 0, 0];
        questions.forEach((q) => {
            const lvl = Number(q.schwierigkeitsgrad);
            if (lvl >= 1 && lvl <= 3) c[lvl - 1]++;
        });
        return c;
    }, [questions]);

    const total = counts.reduce((s, v) => s + v, 0);
    const avg = total === 0
        ? 0
        : (counts[0] * 1 + counts[1] * 2 + counts[2] * 3) / total;

    const maxCount = Math.max(...counts, 1);

    const xs = [PAD_X, PAD_X + CHART_W / 2, PAD_X + CHART_W];
    const ys = counts.map((c) => PAD_Y + CHART_H - (c / maxCount) * CHART_H);

    // Geglättete Bézierkurve durch 3 Punkte
    const mx1 = (xs[0] + xs[1]) / 2;
    const mx2 = (xs[1] + xs[2]) / 2;
    const pathLine = [
        `M ${xs[0]} ${ys[0]}`,
        `C ${mx1} ${ys[0]}, ${mx1} ${ys[1]}, ${xs[1]} ${ys[1]}`,
        `C ${mx2} ${ys[1]}, ${mx2} ${ys[2]}, ${xs[2]} ${ys[2]}`,
    ].join(' ');
    const pathFill = `${pathLine} L ${xs[2]} ${PAD_Y + CHART_H} L ${xs[0]} ${PAD_Y + CHART_H} Z`;

    const labels = ['★1', '★2', '★3'];

    return (
        <div>
            <svg
                width={W}
                height={H}
                viewBox={`0 0 ${W} ${H}`}
                style={{ display: 'block', overflow: 'visible' }}
            >
                <defs>
                    {/* Füllfläche-Verlauf */}
                    <linearGradient id="bc-area" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%"   stopColor="#4ade80" stopOpacity="0.25" />
                        <stop offset="50%"  stopColor="#fbbf24" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="#f87171" stopOpacity="0.25" />
                    </linearGradient>
                    {/* Linien-Verlauf */}
                    <linearGradient id="bc-line" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%"   stopColor="#4ade80" />
                        <stop offset="50%"  stopColor="#fbbf24" />
                        <stop offset="100%" stopColor="#f87171" />
                    </linearGradient>
                    {/* Glow-Filter */}
                    <filter id="bc-glow" x="-30%" y="-60%" width="160%" height="220%">
                        <feGaussianBlur stdDeviation="2.5" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Füllfläche */}
                <path d={pathFill} fill="url(#bc-area)" />

                {/* Kurve */}
                <path
                    d={pathLine}
                    fill="none"
                    stroke="url(#bc-line)"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    filter="url(#bc-glow)"
                />

                {/* Datenpunkte */}
                {xs.map((x, i) => (
                    <g key={i}>
                        <circle cx={x} cy={ys[i]} r={5} fill={DIFF_COLORS[i]} opacity={0.3} />
                        <circle cx={x} cy={ys[i]} r={3.5} fill={DIFF_COLORS[i]} />
                        <text
                            x={x}
                            y={ys[i] - 9}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="700"
                            fill={DIFF_COLORS[i]}
                        >
                            {counts[i]}
                        </text>
                        <text
                            x={x}
                            y={PAD_Y + CHART_H + 13}
                            textAnchor="middle"
                            fontSize="10"
                            fill="#8aa3d4"
                        >
                            {labels[i]}
                        </text>
                    </g>
                ))}
            </svg>

            <div style={{
                textAlign: 'center',
                fontSize: '11px',
                color: '#8aa3d4',
                marginTop: '4px',
            }}>
                {total} Frage{total !== 1 ? 'n' : ''}
                {total > 0 && <> · ⌀ {avg.toFixed(1)}</>}
            </div>
        </div>
    );
}

export default BalanceCurve;
